/**
 * Mobile slide-over over a scrim. Same bands and order as the desktop
 * sidebar — workspace, nav, account — nothing renamed, reordered or
 * dropped. No collapse control: there's nothing to collapse into.
 * Spec: assets/handoffs/design_handoff_app_shell, Rules — mobile.
 *
 * Row heights: the spec calls for 48/44px rows here versus the desktop
 * sidebar's 40/36 (to clear the 44px tap-target minimum). Reusing
 * SidebarNavItem/SidebarNavParent as-is for Phase 1 — their rows stay at
 * desktop height until the drawer gets its own row-height pass; everything
 * else (bands, order, close behaviour) matches the spec.
 *
 * @param {boolean} open
 * @param {function} onClose
 * @param {Object} sidebar - same shape as Sidebar's props
 */
import React, { useEffect } from "react";
import classNames from "classnames";
import SidebarNavItem from "../SidebarNavItem/SidebarNavItem";
import SidebarNavParent from "../SidebarNavParent/SidebarNavParent";
import WorkspaceSwitcher from "../WorkspaceSwitcher/WorkspaceSwitcher";
import AccountRow from "../AccountRow/AccountRow";
import SidebarAlerts from "../../PageTemplate/SidebarNotifications/SidebarAlerts";
import styles from "./NavDrawer.module.scss";

const NavDrawer = ({ open, onClose, sidebar }) => {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const { workspaces, currentWorkspace, onSwitchWorkspace, nav, activeKey, alerts, onDismissAlert, account, impersonating, renderAccountMenu } = sidebar;

  return (
    <>
      <div className={styles.scrim} onClick={onClose} />
      <div className={classNames(styles.drawer)} role="dialog" aria-modal="true">
        <WorkspaceSwitcher workspaces={workspaces} current={currentWorkspace} onSwitch={onSwitchWorkspace} disabled={impersonating} />

        <nav className={styles.nav} aria-label="Main" onClick={(e) => {
          if (e.target.closest("a")) onClose();
        }}>
          <ul className={styles.navList}>
            {nav.top?.map((item) => (
              <li key={item.key}>
                <SidebarNavItem label={item.label} icon={item.icon} to={item.to} active={item.key === activeKey} />
              </li>
            ))}
            {nav.widgets && (
              <SidebarNavParent label={nav.widgets.label} icon={nav.widgets.icon} children={nav.widgets.children} activeKey={activeKey} />
            )}
            {nav.reports && (
              <SidebarNavParent label={nav.reports.label} icon={nav.reports.icon} children={nav.reports.children} activeKey={activeKey} />
            )}
            {nav.standalone?.map((item) => (
              <li key={item.key}>
                <SidebarNavItem label={item.label} icon={item.icon} to={item.to} active={item.key === activeKey} />
              </li>
            ))}
            {nav.platformSettings && (
              <SidebarNavParent label={nav.platformSettings.label} icon={nav.platformSettings.icon} children={nav.platformSettings.children} activeKey={activeKey} />
            )}
          </ul>
        </nav>

        <div className={styles.alerts}>
          <SidebarAlerts alerts={alerts} onDismiss={onDismissAlert} />
        </div>

        <div className={styles.footer}>
          <AccountRow account={account} impersonating={impersonating} renderMenu={renderAccountMenu} />
        </div>
      </div>
    </>
  );
};

export default NavDrawer;
